import { Box, Paper, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  blackColor,
  blackShade19,
  greenColorM01,
  whiteColor,
  yellowShade04,
} from "../../../configs/styles/muiThemes";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const parseCurrency = (currencyStr) => {
  if (!currencyStr) return 0;
  // Ensure it's a string before replacing
  if (typeof currencyStr === "string") {
    return parseFloat(currencyStr.replace(/[\u20B9,]/g, ""));
  }

  // If it's already a number, return as-is
  if (typeof currencyStr === "number") {
    return currencyStr;
  }

  // Default return 0 if it's neither string nor number
  return 0;
};

const formatCurrency = (value) =>
  `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const formatYAxis = (value) => {
  if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)}K`; // Converts to K format
  }
  return `₹${value}`; // For values less than 1000
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={3}
        sx={{ p: "3px 6px", borderRadius: 1, bgcolor: whiteColor }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 500,
            color: blackShade19,
            lineHeight: 1.5,
          }}
        >
          {label}
        </Typography>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 400,
              color: blackShade19,
              lineHeight: 1.5,
            }}
          >
            Total Sales: {formatCurrency(payload[0].payload.totalSales)}
          </Typography>
        </Box>
      </Paper>
    );
  }
  return null;
};

function SaleChartSection({ chartData }) {
  const [data, setData] = useState([]);

  useEffect(() => {
    const processedData = chartData?.map((item) => ({
      month: item?.month,
      orderCount: item?.orderCount ? parseInt(item.orderCount) : 0,
      // totalSales: item?.totalSales,
      totalSales: parseCurrency(item?.totalSales),
    }));

    setData(processedData);
  }, [chartData]);

  return (
    <Stack
      sx={{
        border: "1px solid #E1E8F0",
        borderRadius: "10px",
        backgroundColor: whiteColor,
        p: "10px 15px",
        gap: 1,
      }}
    >
      <Typography
        fontSize={13.5}
        fontWeight={600}
        color={blackShade19}
        lineHeight="20px"
      >
        Sales Analysis
      </Typography>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={data}
          margin={{ top: 20, right: 30, left: 10, bottom: 0 }}
        >
          <defs>
            <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={greenColorM01} stopOpacity={0.8} />
              <stop
                offset="100%"
                stopColor="rgba(255, 255, 255, 1)"
                stopOpacity={0.2}
              />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            horizontal={false}
          />
          <XAxis
            dataKey="month"
            tick={{
              fill: blackShade19,
              fontSize: 11,
              fontWeight: 500,
            }}
            interval={0}
            // tickFormatter={(month) => month.substring(0, 3)}
            tickFormatter={(value) => {
              const parts = value.split("-");
              // return parts.length === 3 ? value : `${parts[0]}-${parts[1]}`;
               return parts.length === 3
                 ? `${parts[0]}`
                //  : `${parts[0]}-${parts[1]}`;
                 : `${parts[0]}`;
            }}
            axisLine={{ stroke: "#000" }}
            tickLine={false}
          />
          <YAxis
            tick={{
              fill: blackShade19,
              fontSize: 11,
              fontWeight: 500,
            }}
            tickFormatter={formatYAxis}
            axisLine={{ stroke: "#000" }}
            tickLine={false}
            // domain={[0, "dataMax + 10000"]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="totalSales"
            stroke={greenColorM01}
            fill="url(#gradient)"
            strokeWidth={3}
            // dot={{ stroke: "#3f53d8", strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: yellowShade04 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Stack>
  );
}

export default SaleChartSection;
